import { useReducer, useCallback } from 'react';
import { useSWRConfig } from 'swr';

import { deepCamelCase, NetworkError } from '@vimeo/gctl-api/fetchers/_helpers';
import { translate } from '@vimeo/i18n-runtime';
import { useViewer } from '@vimeo/viewer';

import { useAlbumStore } from '../state/albumStore';

interface ReducerState {
  called: boolean;
  isSuccess: boolean;
  error: Error | null;
  loading: boolean;
}

type Action =
  | { type: 'REQUEST' }
  | { type: 'SUCCESS' }
  | { type: 'FAILURE'; payload: Error }
  | { type: 'COOKIES_DISABLED'; payload: Error };

const initialState: ReducerState = { called: false, loading: false, error: null, isSuccess: false };

function reducer(state: ReducerState, action: Action) {
  switch (action.type) {
    case 'REQUEST':
      return { ...state, loading: true, called: true };

    case 'SUCCESS':
      return { ...state, loading: false, error: null, isSuccess: true };

    case 'FAILURE':
      return { ...state, loading: false, error: action.payload };

    case 'COOKIES_DISABLED':
      return { ...state, loading: false, called: true, error: action.payload };

    default:
      return state;
  }
}

const getErrorType = (error_code: number) => {
  switch (error_code) {
    case 1:
      return 'incorrect_password';
    default:
      return 'generic_error';
  }
};

const ERROR_MAP = {
  incorrect_password: translate('Sorry, that password was incorrect. Please try again.'),
  generic_error: translate(
    'Unable to validate password. Ensure cookies are enabled in your browser, refresh the page, and try again.',
  ),
};

export const useValidateShowcasePassword = ({
  isCookiesDisabled,
}: {
  isCookiesDisabled: boolean;
}) => {
  const viewer = useViewer();
  const token = viewer?.xsrft;

  const { albumId, isShowcaseEmbedded } = useAlbumStore((state) => ({
    albumId: state.albumId,
    isShowcaseEmbedded: state.isShowcaseEmbedded,
  }));

  const [state, dispatch] = useReducer(reducer, initialState);

  const { mutate } = useSWRConfig();

  const validatePassword = useCallback(
    async (password: string) => {
      if (isCookiesDisabled) {
        dispatch({ type: 'COOKIES_DISABLED', payload: new Error(ERROR_MAP['generic_error']) });
        return;
      }

      let url;
      if (isShowcaseEmbedded) {
        url = `/showcase/${albumId}/password?password=${password}`;
      } else {
        url = location.pathname === '/' ? '/auth' : `/showcase/${albumId}/auth`;
      }

      const fetcher = isShowcaseEmbedded ? getEmbeddedShowcasePassword : postOnsiteShowcasePassword;

      try {
        dispatch({ type: 'REQUEST' });

        /**
         * for onsite showcase (isShowcaseEmbedded is false), `useShowcaseAuth` and `useValidateShowcasePassword` uses the same url `/showcase/${albumId}/auth` for fetcher;
         * if we use the same key, when calling mutate here equals call `mutate` on `useShowcaseAuth`, it has no issues if the password is correct;
         * if the password is incorrect, it will always be just render the password page component, this hook won't be able to get to its error state
         */
        await mutate(
          `key-${url}`,
          fetcher(url, {
            password,
            token,
            referer_url: `/showcase/${albumId}`,
          }),
        );

        dispatch({ type: 'SUCCESS' });
      } catch (error) {
        const errorType = getErrorType(error.res.errorCode);
        const errorMessage = ERROR_MAP[errorType];

        dispatch({ type: 'FAILURE', payload: new Error(errorMessage) });
      }
    },
    [isCookiesDisabled, isShowcaseEmbedded, albumId, token, mutate],
  );

  return { validatePassword, ...state };
};

const getEmbeddedShowcasePassword = async (url: string) => {
  const res = await fetch(url);

  const json = await res.json();

  if (!res.ok || json.password === false) {
    throw new NetworkError('A network error occurred', res.status, {
      ...deepCamelCase(json),
      errorCode: 1,
    });
  }

  return json;
};

const postOnsiteShowcasePassword = async (url: string, data: Record<string, unknown>) => {
  const formData = new FormData();

  Object.entries(data).forEach(([key, value]) => {
    if (typeof value === 'string') {
      formData.append(key, value);
    }
  });

  const res = await fetch(url, {
    method: 'POST',
    body: formData,
  });

  if (!res.ok) {
    const errorRes = await res.json();

    throw new NetworkError('A network error occurred', res.status, errorRes);
  }

  const json = await res.json();

  return deepCamelCase(json);
};
